import { GameState, SkiGameStateEnum } from '../ski';
import { RACE_DISTANCE } from './constants';
import { treeSpawner, treeImagePromise } from './tree-spawner';
import { updateTrackLines, drawTrackLines, resetTrackLines, initializeTrackLines } from './track-line';
import { checkCollisions } from './collision';

// Load background image
const backgroundImg = new Image();
backgroundImg.src = '/fond_ski.png';

const loadingPromise = Promise.all([
    new Promise(resolve => backgroundImg.onload = resolve),
    treeImagePromise,
]);

export interface AnimationContext {
    lastTime: number;
    ctx: CanvasRenderingContext2D;
    gameState: GameState;
    stop: boolean;
    zoom: number;
    onRaceEnd: (completed: boolean) => void;
}

export const getAnimationFunction = (context: AnimationContext) => {
    const animationFunction = async () => {
        await loadingPromise;

        const { ctx, gameState, zoom, onRaceEnd } = context;
        const canvas = ctx.canvas;

        const now = Date.now();
        const deltaTime = now - context.lastTime;
        context.lastTime = now;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw static background
        if (backgroundImg.complete) {
            ctx.drawImage(backgroundImg, 0, 0, zoom * backgroundImg.width, zoom * backgroundImg.height);
        }

        if (gameState.gameState === SkiGameStateEnum.racing) {
            // Update tree spawner
            treeSpawner.update(gameState);

            // Update all trees (use descent speed for scrolling)
            for (const tree of gameState.trees) {
                tree.update(deltaTime, gameState.skier.descentSpeed);
            }

            // Remove off-screen trees
            gameState.trees = gameState.trees.filter(t => !t.isOffScreen());

            // Update track lines (use descent speed for scrolling)
            updateTrackLines(deltaTime, gameState.skier.descentSpeed, gameState.distanceTraveled);

            // Update skier
            gameState.skier.update(deltaTime);

            // Update distance traveled (based on descent speed)
            gameState.distanceTraveled += gameState.skier.descentSpeed * deltaTime / 1000;

            // Check collisions
            checkCollisions(gameState);

            // Check win condition
            if (gameState.distanceTraveled >= RACE_DISTANCE) {
                const raceTime = Date.now() - gameState.raceStartTime;
                gameState.raceTimes.push(raceTime);
                gameState.raceCompleted.push(true);
                gameState.gameState = SkiGameStateEnum.raceFinished;
                onRaceEnd(true);
            }
        }

        // Draw track lines (before trees)
        drawTrackLines(ctx, zoom);

        // Draw trees (sorted by Y for proper layering)
        const sortedTrees = [...gameState.trees].sort((a, b) => a.pos.y - b.pos.y);
        for (const tree of sortedTrees) {
            tree.draw(ctx, zoom);
        }

        // Draw skier (always on top)
        gameState.skier.draw(ctx, zoom);

        // Continue animation unless stopped
        if (!context.stop) {
            requestAnimationFrame(animationFunction);
        }
    };

    return animationFunction;
};

export const resetTreeSpawner = () => {
    treeSpawner.reset();
    resetTrackLines();
};

export const initializeScene = (gameState: GameState) => {
    initializeTrackLines();
    treeSpawner.initializeTrees(gameState);
};
